<template>
  <div class="guide-page">
    <AppBar />

    <!-- Card central -->
    <main class="guide-main">
      <div class="card guide-card">
        <!-- Progress dots -->
        <div class="guide-dots" aria-hidden="true">
          <span
            v-for="(step, i) in steps"
            :key="i"
            class="guide-dot"
            :class="{
              'guide-dot--done': step.done,
              'guide-dot--active': step.active
            }"
          />
        </div>

        <h1 class="guide-title">{{ textTitle }}</h1>
        <p class="sub guide-sub">{{ textSubtitle }}</p>

        <!-- Steps -->
        <div class="guide-steps">
          <!-- 1 · Endereço -->
          <div
            class="guide-step"
            :class="{ 'guide-step--done': !!currentUser.address.id }"
          >
            <div class="guide-step__left">
              <span class="guide-step__icon">
                <svg
                  v-if="currentUser.address.id"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle cx="12" cy="12" r="10" fill="var(--color-success)" />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#fff"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="var(--color-gray-300)"
                    stroke-width="2"
                  />
                  <text
                    x="12"
                    y="16"
                    text-anchor="middle"
                    font-size="10"
                    font-weight="700"
                    fill="var(--color-gray-400)"
                  >
                    1
                  </text>
                </svg>
              </span>
              <span class="guide-step__label">Informar endereço</span>
              <button
                class="guide-step__info-btn"
                id="tip-addr"
                tabindex="-1"
                aria-label="Mais informações"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    d="M12 11v5M12 8h.01"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <b-tooltip target="tip-addr" placement="right">
                Informe o endereço para prosseguir com o seu perfil.
              </b-tooltip>
            </div>
            <div class="guide-step__right">
              <span
                v-if="currentUser.address.id"
                class="badge badge--success badge--no-dot"
              >
                Concluído
              </span>
              <button
                v-else
                class="btn btn--primary btn--sm"
                @click="updateDoctorAddress"
              >
                Prosseguir
              </button>
            </div>
          </div>

          <!-- 2 · Agenda -->
          <div class="guide-step" :class="{ 'guide-step--done': hasSlots }">
            <div class="guide-step__left">
              <span class="guide-step__icon">
                <svg
                  v-if="hasSlots"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle cx="12" cy="12" r="10" fill="var(--color-success)" />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#fff"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="var(--color-gray-300)"
                    stroke-width="2"
                  />
                  <text
                    x="12"
                    y="16"
                    text-anchor="middle"
                    font-size="10"
                    font-weight="700"
                    fill="var(--color-gray-400)"
                  >
                    2
                  </text>
                </svg>
              </span>
              <span class="guide-step__label">
                Configurar agenda de atendimento
              </span>
              <button
                class="guide-step__info-btn"
                id="tip-agenda"
                tabindex="-1"
                aria-label="Mais informações"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    d="M12 11v5M12 8h.01"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <b-tooltip target="tip-agenda" placement="right">
                Configure a sua agenda para poder realizar suas consultas de
                videochamada.
              </b-tooltip>
            </div>
            <div class="guide-step__right">
              <span v-if="hasSlots" class="badge badge--success badge--no-dot">
                Concluído
              </span>
              <button
                v-else-if="currentUser.address.id"
                class="btn btn--primary btn--sm"
                @click="pushTo('ScheduleSettings')"
              >
                Prosseguir
              </button>
              <span v-else class="guide-step__locked">
                Aguardando etapa anterior
              </span>
            </div>
          </div>

          <!-- 3 · Conta bancária -->
          <div
            class="guide-step"
            :class="{ 'guide-step--done': hasBankAccount }"
          >
            <div class="guide-step__left">
              <span class="guide-step__icon">
                <svg
                  v-if="hasBankAccount"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle cx="12" cy="12" r="10" fill="var(--color-success)" />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#fff"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="var(--color-gray-300)"
                    stroke-width="2"
                  />
                  <text
                    x="12"
                    y="16"
                    text-anchor="middle"
                    font-size="10"
                    font-weight="700"
                    fill="var(--color-gray-400)"
                  >
                    3
                  </text>
                </svg>
              </span>
              <span class="guide-step__label">Adicionar conta bancária</span>
              <button
                class="guide-step__info-btn"
                id="tip-bank"
                tabindex="-1"
                aria-label="Mais informações"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    d="M12 11v5M12 8h.01"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <b-tooltip target="tip-bank" placement="right">
                Essa conta bancária deverá ter você como titular — é para ela
                que serão transferidos os valores das consultas.
              </b-tooltip>
            </div>
            <div class="guide-step__right">
              <span
                v-if="hasBankAccount"
                class="badge badge--success badge--no-dot"
              >
                Concluído
              </span>
              <template v-else-if="currentUser.address.id && hasSlots">
                <button
                  class="btn btn--primary btn--sm"
                  @click="createBankAccount"
                >
                  Prosseguir
                </button>
                <button
                  v-if="hasActiveRelationshipWithInstitutions"
                  class="btn btn--ghost btn--sm"
                  @click="skipCompleteRegistration"
                >
                  Pular
                </button>
              </template>
              <span v-else class="guide-step__locked">
                Aguardando etapa anterior
              </span>
            </div>
          </div>

          <!-- 4 · Documentos -->
          <div
            class="guide-step"
            :class="{
              'guide-step--done': hasDocumentsStepCompleted
            }"
          >
            <div class="guide-step__left">
              <span class="guide-step__icon">
                <svg
                  v-if="hasDocumentsStepCompleted"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle cx="12" cy="12" r="10" fill="var(--color-success)" />
                  <path
                    d="M8 12l3 3 5-5"
                    stroke="#fff"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="var(--color-gray-300)"
                    stroke-width="2"
                  />
                  <text
                    x="12"
                    y="16"
                    text-anchor="middle"
                    font-size="10"
                    font-weight="700"
                    fill="var(--color-gray-400)"
                  >
                    4
                  </text>
                </svg>
              </span>
              <span class="guide-step__label">Enviar documentos</span>
              <button
                class="guide-step__info-btn"
                id="tip-docs"
                tabindex="-1"
                aria-label="Mais informações"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    d="M12 11v5M12 8h.01"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <b-tooltip target="tip-docs" placement="right">
                Envie os documentos necessários para validar seu cadastro na
                plataforma.
              </b-tooltip>
            </div>
            <div class="guide-step__right">
              <span
                v-if="hasDocumentsStepCompleted"
                class="badge badge--success badge--no-dot"
              >
                Concluído
              </span>
              <span
                v-else-if="
                  hasDocumentsSent &&
                    !hasDocumentsRejected &&
                    hasDocumentsInAnalysis
                "
                class="badge badge--warning badge--no-dot"
              >
                Em análise
              </span>
              <button
                v-else-if="hasBankAccount"
                class="btn btn--primary btn--sm"
                @click="pushTo('DoctorDocumentsPage')"
              >
                Prosseguir
              </button>
              <span v-else class="guide-step__locked">
                Aguardando etapa anterior
              </span>
            </div>
          </div>
        </div>

        <!-- CTA final -->
        <div v-if="allStepsDone" class="guide-cta">
          <button
            class="btn btn--primary"
            style="width:100%"
            @click="completeUserGuide"
          >
            Ir para o painel
          </button>
        </div>
      </div>
    </main>

    <!-- Modal: endereço -->
    <BModal
      id="updateAddress"
      ref="updateAddress"
      size="lg"
      centered
      hide-footer
      title="Informar endereço"
    >
      <AddressForm @addressCreated="addressCreated" />
    </BModal>

    <!-- Modal: conta bancária -->
    <BModal
      id="bankAccount"
      ref="bankAccount"
      size="lg"
      centered
      hide-footer
      title="Adicionar conta bancária"
    >
      <BankAccountForm @bankAccountCreated="bankAccountCreated" />
    </BModal>

    <!-- Modal: ativar conta (legado) -->
    <BModal
      id="activeAccount"
      ref="activeAccount"
      size="md"
      centered
      hide-footer
      hide-header
    >
      <Account @accountActivated="accountActivated" />
    </BModal>

    <footer class="foot-mini">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <div class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </div>
    </footer>
  </div>
</template>

<script>
import { doctorMethods, doctorDocumentMethods } from "../state/helpers";
import AppBar from "../components/AppBar.vue";
import AddressForm from "./../components/AddressForm";
import Account from "../components/WireCardAccount";
import BankAccountForm from "../components/BankAccountForm";

export default {
  name: "GuidePage",
  components: { AppBar, AddressForm, Account, BankAccountForm },

  data() {
    return {
      hasSlots: false,
      hasBankAccount: false
    };
  },

  mounted() {
    this.fetchDoctorsAvailables({ id: this.currentUser.id })
      .then(response => {
        if (response && response.data && response.data.length)
          this.hasSlots = true;
      })
      .catch(() => {});
    this.$store.commit("auth/SET_USER_GUIDE", true);
    if (this.currentUser.bank_account) this.hasBankAccount = true;
    this.syncDocumentRequirements(this.currentUser.doctor_property);
    this.fetchDocuments().catch(() => {});
  },

  methods: {
    ...doctorMethods,
    ...doctorDocumentMethods,
    pushTo(routeName) {
      this.$router.push({ name: routeName });
    },
    completeUserGuide() {
      this.$store.commit("auth/SET_USER_GUIDE", false);
      this.pushTo("Dashboard");
    },
    skipCompleteRegistration() {
      this.$store.commit("auth/SET_USER_GUIDE", false);
      this.completeUserGuide();
    },
    updateDoctorAddress() {
      this.$bvModal.show("updateAddress");
    },
    addressCreated() {
      this.$bvModal.hide("updateAddress");
    },
    activeDoctorAccount() {
      this.$bvModal.show("activeAccount");
    },
    createBankAccount() {
      this.$bvModal.show("bankAccount");
    },
    bankAccountCreated() {
      this.$store
        .dispatch("user/showUser", this.currentUser.id)
        .then(response => {
          if (response.data.bank_account) this.hasBankAccount = true;
        });
      this.$bvModal.hide("bankAccount");
    },
    accountActivated() {
      this.$bvModal.hide("activeAccount");
    }
  },

  computed: {
    currentUser() {
      return this.$store.state.user.currentUser;
    },
    hasDocumentsSent() {
      try {
        return this.$store.getters["doctorDocument/allDocumentsSent"] || false;
      } catch {
        return false;
      }
    },
    hasDocumentsInAnalysis() {
      try {
        return (
          this.$store.getters["doctorDocument/hasDocumentsInAnalysis"] || false
        );
      } catch {
        return false;
      }
    },
    hasDocumentsRejected() {
      try {
        return (
          this.$store.getters["doctorDocument/hasDocumentsRejected"] || false
        );
      } catch {
        return false;
      }
    },
    hasDocumentsStepCompleted() {
      return (
        this.hasDocumentsSent &&
        !this.hasDocumentsInAnalysis &&
        !this.hasDocumentsRejected
      );
    },
    hasActiveRelationshipWithInstitutions() {
      return this.$store.getters["auth/hasActiveRelationshipWithInstitutions"];
    },
    allStepsDone() {
      return (
        this.hasDocumentsStepCompleted &&
        !!this.currentUser.address.id &&
        this.hasSlots &&
        this.hasBankAccount
      );
    },
    isAwaitingDocumentApproval() {
      return (
        this.hasDocumentsSent &&
        !this.hasDocumentsRejected &&
        this.hasDocumentsInAnalysis &&
        !!this.currentUser.address.id &&
        this.hasSlots &&
        this.hasBankAccount
      );
    },
    steps() {
      return [
        {
          done: !!this.currentUser.address.id,
          active: !this.currentUser.address.id
        },
        {
          done: this.hasSlots,
          active: !!this.currentUser.address.id && !this.hasSlots
        },
        {
          done: this.hasBankAccount,
          active: this.hasSlots && !this.hasBankAccount
        },
        {
          done: this.hasDocumentsStepCompleted,
          active: this.hasBankAccount && !this.hasDocumentsStepCompleted
        }
      ];
    },
    textTitle() {
      const name = (this.currentUser.name || "").split(" ")[0];
      if (this.allStepsDone) return "Tudo pronto!";
      if (this.isAwaitingDocumentApproval) return "Cadastro em análise";
      return `Bem-vindo(a), ${name}`;
    },
    textSubtitle() {
      if (this.allStepsDone)
        return "Seu cadastro está completo. Clique abaixo para acessar o painel.";
      if (this.isAwaitingDocumentApproval)
        return "Seu cadastro está em análise e no momento em que for aprovado você será notificado por e-mail, SMS e/ou WhatsApp.";
      return "Antes de começar, precisamos de algumas informações. É rápido!";
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 22px;
  width: auto;
}

.guide-page {
  min-height: 100vh;
  background: var(--color-bg-page);
  display: flex;
  flex-direction: column;
}

.guide-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-10) var(--space-5);
}

.guide-card {
  width: 100%;
  max-width: 560px;
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

// Progress dots
.guide-dots {
  display: flex;
  gap: var(--space-2);
}

.guide-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-gray-200);
  transition: background 0.2s, width 0.2s;

  &--done {
    background: var(--color-success);
  }

  &--active {
    width: 20px;
    border-radius: 4px;
    background: var(--color-primary);
  }
}

.guide-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--fw-bold);
  color: var(--color-gray-900);
  margin: 0;
}

.guide-sub {
  margin: calc(var(--space-6) * -1) 0 0;
}

// Steps
.guide-steps {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.guide-step {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--color-gray-100);

  &:last-child {
    border-bottom: none;
  }

  &--done .guide-step__label {
    color: var(--color-gray-400);
    text-decoration: line-through;
    text-decoration-color: var(--color-gray-300);
  }

  &__left {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex: 1;
    min-width: 0;
  }

  &__icon {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__label {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-800);
  }

  &__info-btn {
    background: none;
    border: none;
    padding: 0;
    color: var(--color-gray-400);
    cursor: pointer;
    display: flex;
    align-items: center;
    flex: none;

    &:hover {
      color: var(--color-gray-600);
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex: none;
  }

  &__locked {
    font-size: var(--font-size-xs);
    color: var(--color-gray-400);
    white-space: nowrap;
  }
}

.guide-cta {
  margin-top: var(--space-2);
}
</style>
